import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaArrowRight, FaHeart, FaGraduationCap, FaUsers, FaMapMarkerAlt, FaEnvelope, FaPhone } from "react-icons/fa";

import Footer from "../components/common/Footer";
import ReviewSlider from "../components/common/ReviewSlider";
import HighlightText from "../components/core/HomePage/HighlightText";
import ContactFormSection from "../components/core/AboutPage/ContactFormSection";
import LearningGrid from "../components/core/AboutPage/LearningGrid";
import StatsComponent from "../components/core/AboutPage/Stats";
import { DS } from "../utils/designSystem";
import { fadeIn } from "../components/common/motionFrameVarients";

import BannerImage1 from "../assets/Images/abutus1.jpeg";
import BannerImage2 from "../assets/Images/abutus2.jpeg";
import BannerImage3 from "../assets/Images/abutus3.jpeg";

/* ─── SHARED SECTION HEADER ─────────────────────────────────────── */
function SectionHeading({ children, sub, center = true }) {
  return (
    <div className={`flex flex-col gap-3 mb-10 ${center ? "items-center text-center" : "items-start"}`}>
      <h2 className={DS.text.sectionTitle}>{children}</h2>
      <div className={`h-1 w-16 rounded-full bg-yellow-50 ${center ? "mx-auto" : ""}`} />
      {sub && <p className={`${DS.text.sectionSub} ${center ? "max-w-2xl mx-auto" : "max-w-2xl"} mt-1`}>{sub}</p>}
    </div>
  );
}

/* ─── SPAF/KodeVedic relationship cards ─────────────────────────── */
const PILLARS = [
  {
    icon: <FaHeart className="text-pink-200 text-xl" />,
    title: "Born from Purpose",
    body: "KodeVedic wasn't started to build a business - it was built to solve a problem. SPAF saw talented students in Gondia with no access to quality tech education and built the platform to fix that.",
  },
  {
    icon: <FaGraduationCap className="text-caribbeangreen-100 text-xl" />,
    title: "Education as Empowerment",
    body: "We believe a line of code can change a life. By blending modern tech skills with accessible learning, KodeVedic turns opportunity into transformation for underserved communities.",
  },
  {
    icon: <FaUsers className="text-blue-100 text-xl" />,
    title: "Community First",
    body: "Every course, every resource, and every feature on KodeVedic is shaped by community feedback - students, volunteers, and mentors from Gondia and beyond.",
  },
];

/* ─── ABOUT PAGE ─────────────────────────────────────────────────── */
const About = () => {
  return (
    <div className="bg-richblack-900 text-richblack-5">

      {/* ── Hero ──────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-richblack-800 border-b border-richblack-700">
        {/* Ambient glow */}
        <div aria-hidden className="pointer-events-none absolute -top-32 right-0 h-[500px] w-[500px] rounded-full bg-gradient-to-bl from-[#1FA2FF]/8 via-[#12D8FA]/4 to-transparent blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -bottom-16 left-0 h-[300px] w-[300px] rounded-full bg-gradient-to-tr from-yellow-50/5 to-transparent blur-3xl" />

        <div className={`${DS.layout.container} py-20 lg:py-28`}>
          <motion.div
            variants={fadeIn("up", 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
            className="flex flex-col items-center gap-6 text-center max-w-3xl mx-auto"
          >
            {/* Eyebrow */}
            <span className={`${DS.badge.neutral}`}>
              <span className="h-2 w-2 rounded-full bg-caribbeangreen-100" />
              A JoinSPAF Initiative
            </span>

            <h1 className="text-4xl lg:text-5xl font-bold text-richblack-5 leading-tight">
              Where Social Impact Meets{" "}
              <HighlightText text="Tech Education" />
            </h1>

            <p className={`${DS.text.sectionSub} max-w-2xl`}>
              KodeVedic is the EdTech platform of <strong className="text-richblack-5">JoinSPAF</strong> - a grassroots NGO from
              Gondia, Maharashtra dedicated to empowering underserved students through education and opportunity.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mt-2">
              <Link to="/signup" className={DS.btn.primary}>
                Start Learning <FaArrowRight className="text-sm" />
              </Link>
              <a
                href="https://joinspaf.org"
                target="_blank"
                rel="noopener noreferrer"
                className={DS.btn.secondary}
              >
                Visit JoinSPAF
              </a>
            </div>
          </motion.div>

          {/* Banner images */}
          <motion.div
            variants={fadeIn("up", 0.2)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-14 grid grid-cols-3 gap-4"
          >
            <img src={BannerImage1} alt="KodeVedic community" className="rounded-xl object-cover w-full h-44 lg:h-64 shadow-[0_12px_40px_rgba(0,0,0,0.5)]" />
            <img src={BannerImage3} alt="KodeVedic learning" className="rounded-xl object-cover w-full h-44 lg:h-64 shadow-[0_12px_40px_rgba(0,0,0,0.5)] mt-6" />
            <img src={BannerImage2} alt="KodeVedic students" className="rounded-xl object-cover w-full h-44 lg:h-64 shadow-[0_12px_40px_rgba(0,0,0,0.5)]" />
          </motion.div>
        </div>
      </section>

      {/* ── Mission quote ─────────────────────────────────────────── */}
      <section className={`${DS.layout.sectionCompact} border-b border-richblack-700`}>
        <div className={`${DS.layout.containerNarrow} text-center`}>
          <motion.blockquote
            variants={fadeIn("up", 0.1)}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="text-2xl md:text-3xl lg:text-4xl font-semibold text-richblack-5 leading-snug"
          >
            We believe every student - regardless of geography or background -
            deserves access to{" "}
            <HighlightText text="world-class tech education." />
          </motion.blockquote>
          <p className={`${DS.text.body} mt-6 max-w-xl mx-auto`}>
            That belief is why SPAF built KodeVedic: to bridge the digital skills gap
            for students in Gondia and beyond.
          </p>
        </div>
      </section>

      {/* ── Two-entity story: SPAF + KodeVedic ───────────────────── */}
      <section className={`${DS.layout.section} border-b border-richblack-700`}>
        <div className={DS.layout.container}>
          <SectionHeading sub="Two names, one mission - uplift students through education.">
            Our Story
          </SectionHeading>

          <div className="grid gap-8 md:grid-cols-2">
            {/* SPAF card */}
            <motion.div
              variants={fadeIn("right", 0.1)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className={`${DS.card.base} ${DS.card.pad} flex flex-col gap-5`}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-caribbeangreen-900 border border-caribbeangreen-700">
                  <FaHeart className="text-caribbeangreen-100 text-xl" />
                </div>
                <div>
                  <p className="text-xs text-richblack-400 uppercase tracking-widest">The Foundation</p>
                  <h3 className="text-xl font-bold text-richblack-5">JoinSPAF</h3>
                </div>
              </div>
              <p className={DS.text.body}>
                SPAF is a registered social initiative based in <strong className="text-richblack-5">Gondia, Maharashtra</strong>.
                Founded with a deep-rooted passion for social upliftment, SPAF has run{" "}
                <strong className="text-richblack-5">8+ donation drives</strong>, distributed stationery kits
                to hundreds of tribal and rural school children, and directly impacted over{" "}
                <strong className="text-richblack-5">2,500 lives</strong>.
              </p>
              <p className={DS.text.body}>
                Their programmes span motivational school sessions, CSR collaborations, and
                community volunteer campaigns - all centred on one goal: make quality
                education accessible to every child, not just the privileged.
              </p>
              <div className="mt-auto flex flex-col gap-2 text-sm text-richblack-300">
                <span className="flex items-center gap-2">
                  <FaMapMarkerAlt className="text-yellow-50 flex-shrink-0" />
                  Gondia, Maharashtra, India
                </span>
                <span className="flex items-center gap-2">
                  <FaEnvelope className="text-yellow-50 flex-shrink-0" />
                  connect@joinspaf.org
                </span>
                <span className="flex items-center gap-2">
                  <FaPhone className="text-yellow-50 flex-shrink-0" />
                  +91 9209780854
                </span>
              </div>
            </motion.div>

            {/* KodeVedic card */}
            <motion.div
              variants={fadeIn("left", 0.1)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className={`${DS.card.base} ${DS.card.pad} flex flex-col gap-5`}
            >
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-900 border border-blue-700">
                  <FaGraduationCap className="text-blue-100 text-xl" />
                </div>
                <div>
                  <p className="text-xs text-richblack-400 uppercase tracking-widest">The Platform</p>
                  <h3 className="text-xl font-bold text-richblack-5">KodeVedic</h3>
                </div>
              </div>
              <p className={DS.text.body}>
                KodeVedic is SPAF's answer to the digital skills gap in rural India.
                Launched around <strong className="text-richblack-5">2023</strong> from{" "}
                <strong className="text-richblack-5">Fulchur, Gondia</strong>, KodeVedic is a
                full-featured EdTech platform offering coding courses - from Git & GitHub to
                full MERN stack development - blended with India's Vedic philosophy of
                purposeful, mindful learning.
              </p>
              <p className={DS.text.body}>
                The platform provides structured, industry-aligned courses taught by
                practitioners, hands-on projects, compiler tools for 17+ languages,
                and a community built to support learners at every step.
              </p>
              <div className="mt-auto flex flex-col gap-2 text-sm text-richblack-300">
                <span className="flex items-center gap-2">
                  <FaMapMarkerAlt className="text-yellow-50 flex-shrink-0" />
                  Near ZP School, Fulchur, Gondia, Maharashtra
                </span>
                <span className="flex items-center gap-2">
                  <FaGraduationCap className="text-yellow-50 flex-shrink-0" />
                  Web Dev · MERN · Git · Data Structures · 17+ Languages
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Why KodeVedic pillars ─────────────────────────────────── */}
      <section className={`${DS.layout.section} border-b border-richblack-700`}>
        <div className={DS.layout.container}>
          <SectionHeading sub="Three principles guide everything we build and every course we create.">
            What We Stand For
          </SectionHeading>

          <div className="grid gap-6 sm:grid-cols-3">
            {PILLARS.map(({ icon, title, body }, i) => (
              <motion.div
                key={title}
                variants={fadeIn("up", 0.05 * i)}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                className={`${DS.card.base} ${DS.card.hover} ${DS.card.pad} flex flex-col gap-4`}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-richblack-700">
                  {icon}
                </div>
                <h3 className="text-lg font-semibold text-richblack-5">{title}</h3>
                <p className="text-sm text-richblack-300 leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats ─────────────────────────────────────────────────── */}
      <StatsComponent />

      {/* ── Learning grid ─────────────────────────────────────────── */}
      <section className={`${DS.layout.section} border-b border-richblack-700`}>
        <div className={DS.layout.container}>
          <SectionHeading sub="How KodeVedic prepares you for the industry.">
            Our Learning Approach
          </SectionHeading>
          <LearningGrid />
        </div>
      </section>

      {/* ── Contact ───────────────────────────────────────────────── */}
      <section className={`${DS.layout.section} border-b border-richblack-700`}>
        <div className={DS.layout.containerNarrow}>
          <ContactFormSection />
        </div>
      </section>

      {/* ── Reviews ───────────────────────────────────────────────── */}
      <section className={`${DS.layout.sectionCompact} border-b border-richblack-700`}>
        <div className={DS.layout.container}>
          <SectionHeading>Reviews from Our Learners</SectionHeading>
          <ReviewSlider />
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
